import { ArrowLeft, Printer } from "lucide-react";
import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import type { TicketConversationExportDto } from "../../../shared/ticket-export";
import { TicketConversationExport } from "../../components/TicketConversationExport";
import { PageError, PageLoader } from "../../components/StateViews";
import { useSession } from "../../context/SessionContext";
import { useAsync } from "../../hooks/useAsync";
import { apiRequest } from "../../lib/api";
import { canExportTicketConversation, exportTimezone } from "../../lib/ticketExport";

export function AgentTicketExportPage() {
  const { user } = useSession();
  if (!canExportTicketConversation(user)) return <PageError message="Only administrators and managers with ticket-view permission can export conversations." />;
  return <ExportPreview timeZone={exportTimezone(user?.timezone || "Asia/Ho_Chi_Minh")} />;
}

function ExportPreview({ timeZone }: { timeZone: string }) {
  const { id = "" } = useParams();
  const result = useAsync(async signal => {
    const response = await apiRequest<{ ticket: TicketConversationExportDto }>(`/agent/tickets/${encodeURIComponent(id)}/export-conversation`, { signal, cache: "no-store" });
    return response.ticket;
  }, [id]);
  useEffect(() => {
    document.documentElement.classList.add("ticket-export-mode");
    return () => document.documentElement.classList.remove("ticket-export-mode");
  }, []);
  useEffect(() => {
    const previousTitle = document.title;
    const number = result.data?.number.replace(/[^a-zA-Z0-9_-]/g, "");
    if (number) document.title = `Ticket-${number}-conversation`;
    return () => { document.title = previousTitle; };
  }, [result.data?.number]);

  return <section className="ticket-export-page">
    <div className="ticket-export-toolbar">
      <Link className="back-link" to={`/agent/tickets/${encodeURIComponent(id)}`}><ArrowLeft size={16} aria-hidden="true" /> Back to ticket</Link>
      <div><h1>Export conversation to PDF</h1><p>Review the document below, then choose <strong>Save as PDF</strong> in the print dialog. Turn off browser headers and footers for a clean copy.</p><p>Email addresses are hidden, but other personal or confidential information may remain in the conversation. Review it before sharing.</p></div>
      <button className="button button--primary" type="button" disabled={result.loading || Boolean(result.error) || !result.data} onClick={() => window.print()}><Printer size={18} aria-hidden="true" /> Print / Save as PDF</button>
    </div>
    {result.loading ? <PageLoader label="Preparing the complete conversation..." /> : result.error || !result.data
      ? <PageError message={result.error || "Could not prepare the conversation."} onRetry={() => void result.refresh()} />
      : <TicketConversationExport ticket={result.data} timeZone={timeZone} />}
  </section>;
}
